import { generateInitSelectedRankDescOptions } from 'pages/product/create-page-v2/common';
import type { CascaderOption } from 'types/base';
import type { CreateRecyclingConsignmentOrderV2SchemaType } from '../common';
import { getBagTypeNameFromCategoryIdList } from './form-items/authentication-config';
import type { GlobalInfoState } from 'store/slices/globalInfoSlice';

type ProductItem =
  CreateRecyclingConsignmentOrderV2SchemaType['productList'][number];

type ReviewStatus = 'pending' | 'genuine' | 'fake';

type NormalizeAuthenticationTaskProductParams = {
  currentProduct: ProductItem;
  taskData: Record<string, any>;
  authenticationBagId: number;
  productCategoryCascaderOptions: CascaderOption[];
  colorSelectOptions?: GlobalInfoState['colorSelectOptions'];
  reviewStatus?: ReviewStatus;
};

export const reviewStatusToIsFakeBag = (
  reviewStatus?: ReviewStatus,
): boolean | undefined => {
  if (reviewStatus === 'genuine') return false;
  if (reviewStatus === 'fake') return true;
  return undefined;
};

const resolveIsFakeBag = ({
  currentProduct,
  reviewStatus,
}: {
  currentProduct: ProductItem;
  reviewStatus?: ReviewStatus;
}) => reviewStatusToIsFakeBag(reviewStatus) ?? currentProduct.isFakeBag;

const hasAuthenticationTaskData = (taskData: Record<string, any>) =>
  !!taskData && Object.keys(taskData).length > 0;

// 若 current 已有值（非空数组或非空字符串），保留 current，否则使用 fallback
const keepIfHasValue = <T>(current: T, fallback: T): T => {
  if (Array.isArray(current)) return current.length > 0 ? current : fallback;
  if (typeof current === 'string') return current !== '' ? current : fallback;
  return current ?? fallback;
};

const createEmptyLocaleValue = () => ({
  en: '',
  ja: '',
  zh_CN: '',
  zh_TW: '',
});

const createDefaultProductItem = (id: string): ProductItem => ({
  id,
  productCategoryIdList: [],
  brandName: 'Hermes',
  name: createEmptyLocaleValue(),
  rank: '',
  rankDesc: createEmptyLocaleValue(),
  attrSize: createEmptyLocaleValue(),
  selectedRankDescOptions: generateInitSelectedRankDescOptions(),
  color: [],
  colors: '',
  material: [],
  hardware: [],
  stamp: '',
  fullStamp: '',
  accessory: [],
  finalSalePrice: null,
  finalRecyclePrice: null,
  firstValuationShopRemark: '',
  guestRemarks: '',
  firstValuationStock: 1,
  firstValuationStockRemark: '',
  productPics: '{"front":"","back":"","interior":"","blindStamp":""}',
  storeReceiptPics: [],
  aiIdentifyPics: [],
  authenticationBagId: undefined,
  isFakeBag: undefined,
  fakeBagReason: undefined,
  reviewerName: undefined,
  quoteStaffId: undefined,
  quoteTime: undefined,
  initXmAssets: false,
  facebookEnabled: undefined,
  bagTypeName: undefined,
});

const deriveColorsFromColor = (
  color: ProductItem['color'],
  colorSelectOptions: GlobalInfoState['colorSelectOptions'] = [],
) => {
  if (!color || color.length === 0) return '';
  if (color.length > 1) return 'multi-color';

  const colorOption = colorSelectOptions.find((d) => d.value === color[0]);
  return colorOption?.refProperty1 || colorOption?.refProperty2 || '';
};

export const normalizeAuthenticationTaskProduct = ({
  currentProduct,
  taskData,
  authenticationBagId,
  productCategoryCascaderOptions,
  colorSelectOptions,
  reviewStatus,
}: NormalizeAuthenticationTaskProductParams): ProductItem => {
  const defaultProduct = createDefaultProductItem(currentProduct.id);

  const fromTask = {
    ...defaultProduct,
    ...taskData,
  };

  const productCategoryIdList = keepIfHasValue(
    currentProduct.productCategoryIdList,
    fromTask.productCategoryIdList,
  );

  const bagTypeName =
    taskData?.bagTypeName ||
    getBagTypeNameFromCategoryIdList(
      productCategoryIdList,
      productCategoryCascaderOptions,
    );
  const color = keepIfHasValue(currentProduct.color, fromTask.color);
  const colors =
    keepIfHasValue(currentProduct.colors, fromTask.colors) ||
    deriveColorsFromColor(color, colorSelectOptions);
  const isFakeBag = resolveIsFakeBag({ currentProduct, reviewStatus });

  return {
    ...fromTask,
    id: currentProduct.id,
    productCategoryIdList,
    brandName: keepIfHasValue(currentProduct.brandName, fromTask.brandName),
    name: {
      ...defaultProduct.name,
      ...(taskData?.name || {}),
      ...(currentProduct.name?.en ? currentProduct.name : {}),
    },
    rank: keepIfHasValue(currentProduct.rank, fromTask.rank),
    rankDesc: {
      ...defaultProduct.rankDesc,
      ...(taskData?.rankDesc || {}),
      ...(currentProduct.rankDesc?.en ? currentProduct.rankDesc : {}),
    },
    attrSize: {
      ...defaultProduct.attrSize,
      ...(taskData?.attrSize || {}),
      ...(currentProduct.attrSize?.en ? currentProduct.attrSize : {}),
    },
    selectedRankDescOptions:
      taskData?.selectedRankDescOptions ||
      defaultProduct.selectedRankDescOptions,
    color,
    colors,
    colorRemark: keepIfHasValue(
      currentProduct.colorRemark,
      fromTask.colorRemark,
    ),
    material: keepIfHasValue(currentProduct.material, fromTask.material),
    materialRemark: keepIfHasValue(
      currentProduct.materialRemark,
      fromTask.materialRemark,
    ),
    hardware: keepIfHasValue(currentProduct.hardware, fromTask.hardware),
    hardwareRemark: keepIfHasValue(
      currentProduct.hardwareRemark,
      fromTask.hardwareRemark,
    ),
    stamp: keepIfHasValue(currentProduct.stamp, fromTask.stamp),
    stampRemark: keepIfHasValue(
      currentProduct.stampRemark,
      fromTask.stampRemark,
    ),
    accessory: keepIfHasValue(currentProduct.accessory, fromTask.accessory),
    aiIdentifyPics: [],
    authenticationBagId,
    isFakeBag,
    fakeBagReason: currentProduct.fakeBagReason,
    reviewerName: currentProduct.reviewerName,
    initXmAssets: isFakeBag
      ? false
      : (currentProduct.initXmAssets ?? fromTask.initXmAssets ?? false),
    bagTypeName,
    // 以下字段与"绑定的是哪个鉴定任务"无关，
    // 绝不能被历史任务数据或空白模板覆盖，必须原样保留当前值
    fullStamp: currentProduct.fullStamp,
    quantity: currentProduct.quantity,
    price: currentProduct.price,
    quoteStaffId: currentProduct.quoteStaffId,
    quoteTime: currentProduct.quoteTime,
    finalSalePrice: currentProduct.finalSalePrice,
    finalRecyclePrice: currentProduct.finalRecyclePrice,
    firstValuationShopRemark: currentProduct.firstValuationShopRemark,
    guestRemarks: currentProduct.guestRemarks,
    firstValuationStock: currentProduct.firstValuationStock,
    firstValuationStockRemark: currentProduct.firstValuationStockRemark,
    productPics: currentProduct.productPics,
    storeReceiptPics: currentProduct.storeReceiptPics,
  };
};

export const bindAuthenticationTaskToProduct = ({
  currentProduct,
  taskData,
  authenticationBagId,
  productCategoryCascaderOptions,
  colorSelectOptions,
  reviewStatus,
}: NormalizeAuthenticationTaskProductParams): ProductItem => {
  if (!hasAuthenticationTaskData(taskData)) {
    const isFakeBag = resolveIsFakeBag({ currentProduct, reviewStatus });
    return {
      ...currentProduct,
      authenticationBagId,
      isFakeBag,
      initXmAssets: isFakeBag ? false : currentProduct.initXmAssets,
    };
  }

  return normalizeAuthenticationTaskProduct({
    currentProduct,
    taskData,
    authenticationBagId,
    productCategoryCascaderOptions,
    colorSelectOptions,
    reviewStatus,
  });
};
